/**
 * Wishlist page — the user's saved-product shelf.
 *
 * Route: /wishlist (also aliased to /dashboard/library for the bottom nav).
 * Layout inherits the redesigned Shop shell so the header, cart, mobile
 * bottom-nav and theme all stay consistent.
 */
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { Heart, ShoppingBag, Sparkles, Share2, Check, BellOff, Bell } from "lucide-react";
import { api, API_BASE, getToken } from "@/lib/api";
import { useWishlist } from "@/hooks/useWishlist";
import { ShopShell, PremiumProductCard } from "@/pages/Shop";
import type { Product } from "@/lib/types";
import { toast } from "sonner";
import { QRShareModal } from "@/components/shared/QRShareModal";

// Fallback axios so the page works both authed (uses the api instance so
// tokens attach automatically) and gracefully-empty when unauthed.
const _http = axios.create({ baseURL: API_BASE, withCredentials: true });

interface WishlistResp {
  items: Product[];
  total: number;
}

export const WishlistPage: React.FC = () => {
  const nav = useNavigate();
  const authed = !!getToken();
  const { count } = useWishlist();

  const data = useQuery<WishlistResp>({
    queryKey: ["wishlist"],
    queryFn: async () => (await api.get("/shop/wishlist")).data,
    enabled: authed,
  });

  return (
    <ShopShell
      title="Your Wishlist — SociBaba"
      description="Every product you've hearted, saved to your personal SIBA-scored shelf."
      hideFooter
      showBottomNav
    >
      {/* Header strip */}
      <div className="mb-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5 flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-orange-500 font-display font-bold">
            <Heart className="h-3 w-3 fill-orange-500" />
            Your Shelf
          </div>
          <h1
            className="font-serif-display font-bold text-2xl sm:text-3xl text-slate-900 dark:text-white tracking-tight mt-1"
            data-testid="wishlist-header-title"
          >
            {authed && count > 0 ? (
              <>
                {count} saved product{count === 1 ? "" : "s"}
              </>
            ) : (
              <>Your Wishlist</>
            )}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Every SIBA-scored product you've hearted, ready to revisit or share.
          </p>
        </div>
        <Link
          to="/shop"
          data-testid="wishlist-cta-browse"
          className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 dark:bg-white dark:hover:bg-slate-100 text-white dark:text-slate-900 text-sm font-display font-semibold px-5 py-2.5 transition-colors"
        >
          <ShoppingBag className="h-4 w-4" />
          Browse the shop
        </Link>
        {authed && <GlobalMuteToggle />}
        {authed && (data.data?.items?.length ?? 0) > 0 && <ShareButton />}
      </div>

      {!authed ? (
        <SignInPrompt />
      ) : data.isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="bg-slate-100 dark:bg-slate-800 animate-pulse border border-slate-200 dark:border-slate-800 aspect-[3/4]"
            />
          ))}
        </div>
      ) : (data.data?.items?.length ?? 0) === 0 ? (
        <EmptyState onBrowse={() => nav("/shop")} />
      ) : (
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
          data-testid="wishlist-grid"
        >
          {(data.data?.items ?? []).map((p, i) => (
            <div key={p.product_id} className="relative">
              <PremiumProductCard p={p} listName="wishlist" index={i} />
              <MuteChip productId={p.product_id} muted={!!(p as any)._alert_muted} slug={p.slug} />
            </div>
          ))}
        </div>
      )}
    </ShopShell>
  );
};

// Global mute toggle — flips notification_prefs.wishlist_alerts_muted for
// the current user. When ON, no wishlist-related emails (price-drops or
// landslides) are dispatched. Per-item mute still works if the user wants
// selective silence.
const GlobalMuteToggle: React.FC = () => {
  const qc = useQueryClient();
  const q = useQuery<{ wishlist_alerts_muted: boolean }>({
    queryKey: ["wishlist-prefs"],
    queryFn: async () => (await api.get("/shop/wishlist/prefs")).data,
  });
  const muted = !!q.data?.wishlist_alerts_muted;
  const mut = useMutation({
    mutationFn: async (v: boolean) => (await api.put("/shop/wishlist/prefs", { wishlist_alerts_muted: v })).data,
    onSuccess: (_r, v) => {
      qc.setQueryData(["wishlist-prefs"], { wishlist_alerts_muted: v });
      toast(v ? "All wishlist emails muted" : "Wishlist emails re-enabled");
    },
  });
  return (
    <button
      type="button"
      onClick={() => mut.mutate(!muted)}
      disabled={mut.isPending}
      data-testid="wishlist-global-mute"
      className={`inline-flex items-center gap-1.5 border text-sm font-display font-semibold px-4 py-2.5 transition-colors ${
        muted
          ? "border-rose-300 dark:border-rose-800 bg-rose-50 dark:bg-rose-950/40 text-rose-600 dark:text-rose-400"
          : "border-slate-200 dark:border-slate-700 hover:border-slate-900 dark:hover:border-white text-slate-500 dark:text-slate-400"
      }`}
      title={muted ? "Alerts are OFF — click to enable" : "Alerts are ON — click to mute all wishlist emails"}
    >
      {muted ? <BellOff className="h-4 w-4" /> : <Bell className="h-4 w-4" />}
      {muted ? "Alerts off" : "Alerts on"}
    </button>
  );
};

// Per-item mute — pinned to the top-right of each card in the wishlist
// grid. Overrides the global toggle for one product.
const MuteChip: React.FC<{ productId: string; muted: boolean; slug?: string }> = ({ productId, muted, slug }) => {
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: async () => (await api.post(`/shop/wishlist/mute/${productId}`)).data,
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["wishlist"] });
      toast(r?.muted ? "Alerts muted for this product" : "Alerts re-enabled", { duration: 1600 });
    },
  });
  return (
    <button
      type="button"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); mut.mutate(); }}
      aria-pressed={muted}
      data-testid={`wishlist-mute-${slug || productId}`}
      className={`absolute z-10 top-2 right-2 h-8 w-8 flex items-center justify-center shadow-sm border transition-all hover:scale-105 active:scale-95 ${
        muted
          ? "bg-rose-500 border-rose-500 text-white"
          : "bg-white/95 dark:bg-slate-900/95 border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:text-rose-500"
      }`}
      title={muted ? "Alerts muted for this product" : "Mute price + score alerts for this product"}
    >
      {muted ? <BellOff className="h-4 w-4" /> : <Bell className="h-4 w-4" strokeWidth={2} />}
    </button>
  );
};

const ShareButton: React.FC = () => {
  const [qrOpen, setQrOpen] = React.useState(false);
  const [shareUrl, setShareUrl] = React.useState("");
  const mut = useMutation<{ token: string; url_path: string }>({
    mutationFn: async () => (await api.get("/shop/wishlist/share")).data,
  });
  const onShare = async () => {
    try {
      const r = await mut.mutateAsync();
      const url = `${window.location.origin}${r.url_path}`;
      setShareUrl(url);
      setQrOpen(true);
    } catch {
      toast("Couldn't create share link. Please try again.");
    }
  };
  return (
    <>
      <button
        type="button"
        onClick={onShare}
        disabled={mut.isPending}
        data-testid="wishlist-share-btn"
        className="inline-flex items-center gap-1.5 border border-slate-200 dark:border-slate-700 hover:border-slate-900 dark:hover:border-white text-slate-900 dark:text-white text-sm font-display font-semibold px-5 py-2.5 transition-colors disabled:opacity-50"
      >
        <Share2 className="h-4 w-4" />
        Share shelf
      </button>
      <QRShareModal
        open={qrOpen}
        onOpenChange={setQrOpen}
        url={shareUrl}
        title="Share your wishlist"
        subtitle="Anyone with this link (or QR code) can browse your saved shelf — read only."
        testIdPrefix="wishlist-qr"
      />
    </>
  );
};

const SignInPrompt: React.FC = () => (
  <div
    className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-12 text-center"
    data-testid="wishlist-signin-prompt"
  >
    <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-orange-500/10 mb-4">
      <Heart className="h-6 w-6 text-orange-500" />
    </div>
    <h2 className="font-serif-display font-bold text-xl text-slate-900 dark:text-white">
      Sign in to unlock your wishlist
    </h2>
    <p className="text-sm text-slate-500 dark:text-slate-400 mt-2 max-w-md mx-auto">
      Save every SIBA-scored product you love to a personal shelf that syncs
      across every device. Free forever.
    </p>
    <div className="mt-5 inline-flex items-center gap-2 flex-wrap justify-center">
      <Link
        to="/login?next=/wishlist"
        data-testid="wishlist-signin-btn"
        className="inline-flex items-center gap-1.5 bg-orange-500 hover:bg-orange-600 text-white text-sm font-display font-semibold px-5 py-2.5 transition-colors"
      >
        Sign in
      </Link>
      <Link
        to="/signup?next=/wishlist"
        className="border border-slate-200 dark:border-slate-700 hover:border-slate-900 dark:hover:border-white text-slate-900 dark:text-white text-sm font-display font-semibold px-5 py-2.5 transition-colors"
      >
        Create an account
      </Link>
    </div>
  </div>
);

const EmptyState: React.FC<{ onBrowse: () => void }> = ({ onBrowse }) => (
  <div
    className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-12 text-center"
    data-testid="wishlist-empty"
  >
    <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-orange-500/10 mb-4">
      <Sparkles className="h-6 w-6 text-orange-500" />
    </div>
    <h2 className="font-serif-display font-bold text-xl text-slate-900 dark:text-white">
      Nothing saved yet.
    </h2>
    <p className="text-sm text-slate-500 dark:text-slate-400 mt-2 max-w-md mx-auto">
      Tap the ♥ heart on any product card in the shop to bookmark it for later.
    </p>
    <button
      onClick={onBrowse}
      data-testid="wishlist-empty-cta"
      className="mt-5 inline-flex items-center gap-1.5 bg-orange-500 hover:bg-orange-600 text-white text-sm font-display font-semibold px-5 py-2.5 transition-colors"
    >
      <ShoppingBag className="h-4 w-4" />
      Browse the shop
    </button>
  </div>
);

export default WishlistPage;
